import React from 'react';
import { Clock, ArrowRight, BookOpen } from 'lucide-react';
import { BLOG_POST_METAS } from '../blog/blogPostsMeta';

/**
 * LatestFromBlogSection — homepage block linking to the 4 most recent
 * blog posts with their accent colors. Two SEO purposes:
 *   1. Gives every blog post an inbound link from the highest-PageRank URL
 *      on the site (the homepage). Without this, individual posts had only
 *      one inbound link (from /blog), which is the classic
 *      "Discovered – currently not indexed" trigger in Search Console.
 *   2. Plain `<a href>` tags ensure Googlebot can crawl the post URLs even
 *      on its first JS-disabled pass.
 *
 * The 8 editorial posts come from the static `BLOG_POSTS` registry; this
 * keeps the section render-stable for SSR / pre-rendering.
 */
export function LatestFromBlogSection() {
  const posts = [...BLOG_POST_METAS]
    .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
    .slice(0, 4);

  return (
    <section
      id="latest-blog"
      className="py-20 px-6 sm:px-8 bg-[#0a0e14] border-t border-[#20262f]"
      aria-labelledby="latest-blog-heading"
      data-testid="landing-latest-blog"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#cafd00] font-bold mb-2">From the Blog</p>
            <h2 id="latest-blog-heading" className="text-3xl sm:text-5xl font-headline font-bold tracking-tighter text-[#f1f3fc]">
              Evidence-based guides
            </h2>
          </div>
          <a
            href="/blog.html"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-[#cafd00] hover:text-[#cafd00]/80 transition-colors"
            data-testid="landing-blog-all-link"
          >
            All articles <ArrowRight className="h-4 w-4" />
          </a>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4" data-testid="landing-blog-cards">
          {posts.map((p) => (
            <a
              key={p.slug}
              href={`/blog/${p.slug}.html`}
              className="group bg-[#0f141a] border border-[#20262f] rounded-3xl p-5 hover:border-[#5a5f6a] transition-all flex flex-col"
              style={{ boxShadow: `inset 4px 0 0 ${p.accent}40` }}
              data-testid={`landing-blog-card-${p.slug}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded"
                  style={{ backgroundColor: `${p.accent}15`, color: p.accent }}
                >
                  {p.category}
                </span>
                <span className="text-[10px] text-[#a8abb3] flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {p.readTime}
                </span>
              </div>
              <h3 className="text-base font-bold font-['Space_Grotesk'] text-[#f1f3fc] mb-2 group-hover:text-[#cafd00] transition-colors leading-tight">
                {p.title}
              </h3>
              <p className="text-xs text-[#a8abb3] leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
              <p className="text-[10px] text-[#a8abb3] mt-3">
                {new Date(p.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
              </p>
            </a>
          ))}
        </div>

        <a
          href="/blog.html"
          className="sm:hidden mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#cafd00] hover:text-[#cafd00]/80 transition-colors"
          data-testid="landing-blog-all-link-mobile"
        >
          <BookOpen className="h-4 w-4" /> See all articles <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </section>
  );
}

export default LatestFromBlogSection;
